import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { CloudEnvironment, CloudKind, CloudMapNode } from '../api/types'
import { Card, EmptyState, ErrorBanner, Spinner, timeAgo } from '../components/common'
import Mappa, { type ArcoMappa, type GruppoNodo, type NodoMappa, type VoceLegenda } from '../components/Mappa'
import { leggiLayout, scriviLayout, type LayoutMappa, type Posizione } from '../components/mappaLayout'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * Ambienti cloud, lo specchio di Ambienti virtuali per AWS, Azure, Google Cloud
 * e Oracle Cloud:
 *
 * 1. la striscia dei totali su tutti gli account (istanze, esposizione verso
 *    Internet, gruppi di sicurezza aperti, costo del mese);
 * 2. una card per **account** (sottoscrizione, progetto, compartimento) con i
 *    suoi numeri e il costo, da cui si sceglie;
 * 3. la **mappa dinamica** dell'account sul modulo Mappa condiviso: account →
 *    regioni → reti → subnet → risorse, i volumi sulle istanze; si filtra per
 *    regione e per genere, si cerca, si apre la risorsa.
 *
 * Dati da `cloud_map_service` (`/cis/cloud-map`).
 */
export default function CloudMap() {
  const ambienti = useAsync(() => api.cloudMap(), [])
  const [chiave, setChiave] = useState<string | null>(null)
  const dati = ambienti.data ?? []
  const ambiente = useMemo(() => dati.find((a) => a.key === chiave) ?? dati[0], [dati, chiave])

  if (ambienti.loading && !ambienti.data) return <Spinner />

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Ambienti cloud')}</h1>
          <p className="page__subtitle">
            {t('Visualizza gli account cloud, le regioni, le reti e le relative risorse rilevate dai connettori e mostra in forma grafica le relazioni e le dipendenze tra componenti infrastrutturali, servizi e workload distribuiti.')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link className="chip" to="/cinventory">{t('Inventario cloud')}</Link>
          <Link className="chip" to="/cost-center">{t('Costi Cloud')}</Link>
        </div>
      </header>

      <ErrorBanner error={ambienti.error} />

      {dati.length === 0 ? (
        <EmptyState
          title={t('Nessun ambiente cloud')}
          hint={t('Compaiono qui dopo una discovery con un connettore cloud (AWS, Azure, Google Cloud, Oracle Cloud).')}
        />
      ) : (
        <>
          <Cruscotto ambienti={dati} />
          <div className="venv-cards">
            {dati.map((a) => (
              <CardAccount key={a.key} ambiente={a} scelto={a.key === ambiente?.key} onScegli={() => setChiave(a.key)} />
            ))}
          </div>
          {ambiente && <MappaAccount key={ambiente.key} ambiente={ambiente} />}
        </>
      )}
    </>
  )
}

// ------------------------------------------------------------------ cruscotto

function denaro(importo: number, valuta: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: valuta || 'USD', maximumFractionDigits: 0 }).format(importo)
  } catch {
    return `${Math.round(importo)} ${valuta}`
  }
}

function Cruscotto({ ambienti }: { ambienti: CloudEnvironment[] }) {
  const tot = ambienti.reduce(
    (acc, a) => {
      acc.istanze += a.summary.instances
      acc.accese += a.summary.instances_running
      acc.pubbliche += a.summary.public
      acc.sg += a.summary.open_security_groups
      const s = a.summary
      acc.risorse += s.instances + s.networks + s.subnets + s.volumes + s.buckets + s.databases
        + s.load_balancers + s.functions + s.security_groups + s.other
      if (a.cost_month) acc.costi[a.cost_month.currency] = (acc.costi[a.cost_month.currency] ?? 0) + a.cost_month.amount
      return acc
    },
    { istanze: 0, accese: 0, pubbliche: 0, sg: 0, risorse: 0, costi: {} as Record<string, number> },
  )
  const valute = Object.entries(tot.costi).sort((a, b) => b[1] - a[1])
  const provider = new Set(ambienti.map((a) => a.provider)).size
  return (
    <div className="venv-kpi">
      <div className="venv-kpi__tile">
        <span className="venv-kpi__value">{ambienti.length}</span>
        <span className="venv-kpi__label">{t('Account')}</span>
        <span className="venv-kpi__sub">{t('{n} provider', { n: provider })} · {t('{n} risorse', { n: tot.risorse })}</span>
      </div>
      <div className="venv-kpi__tile">
        <span className="venv-kpi__value">{tot.accese}<span className="venv-kpi__den">/{tot.istanze}</span></span>
        <span className="venv-kpi__label">{t('Istanze accese')}</span>
      </div>
      <div className="venv-kpi__tile">
        <span className={`venv-kpi__value venv-kpi__value--${tot.pubbliche ? 'warn' : 'ok'}`}>{tot.pubbliche}</span>
        <span className="venv-kpi__label">{t('Esposte a Internet')}</span>
        <span className="venv-kpi__sub" title={t('Istanze, basi dati e bilanciatori con indirizzo pubblico')}>{t('con indirizzo pubblico')}</span>
      </div>
      <div className="venv-kpi__tile">
        <span className={`venv-kpi__value venv-kpi__value--${tot.sg ? 'err' : 'ok'}`}>{tot.sg}</span>
        <span className="venv-kpi__label">{t('Security group aperti')}</span>
        <span className="venv-kpi__sub" title={t('Con regole in ingresso da qualunque origine')}>{t('aperti a tutti')}</span>
      </div>
      <div className="venv-kpi__tile">
        <span className="venv-kpi__value">{valute.length ? denaro(valute[0][1], valute[0][0]) : '—'}</span>
        <span className="venv-kpi__label">{t('Costo del mese')}</span>
        <span className="venv-kpi__sub">
          {valute.length > 1 ? valute.slice(1).map(([v, n]) => denaro(n, v)).join(' · ') : t('dal primo del mese')}
        </span>
      </div>
    </div>
  )
}

/**
 * Un identificativo lungo accorciato nel mezzo: un OCID o un progetto Google
 * superano la larghezza di una card, e la parte che distingue due account è
 * spesso la coda. Il testo intero resta nel tooltip.
 */
export function accorcia(testo: string, max = 26): string {
  if (testo.length <= max) return testo
  const coda = Math.max(6, Math.floor(max / 3))
  return `${testo.slice(0, max - coda - 1)}…${testo.slice(-coda)}`
}

function CardAccount({ ambiente, scelto, onScegli }: { ambiente: CloudEnvironment; scelto: boolean; onScegli: () => void }) {
  const s = ambiente.summary
  const altri = [
    s.databases ? t('{n} basi dati', { n: s.databases }) : null,
    s.load_balancers ? t('{n} bilanciatori', { n: s.load_balancers }) : null,
    s.buckets ? t('{n} storage', { n: s.buckets }) : null,
    s.functions ? t('{n} funzioni', { n: s.functions }) : null,
    s.volumes ? t('{n} volumi ({gb} GB)', { n: s.volumes, gb: s.storage_gb }) : null,
  ].filter(Boolean) as string[]
  const idDiverso = ambiente.account && ambiente.label !== ambiente.account
  return (
    <div className={`venv-card cloud-card${scelto ? ' venv-card--on' : ''}`} onClick={onScegli} role="button" tabIndex={0}
         onKeyDown={(e) => { if (e.key === 'Enter') onScegli() }}>
      <span className="cloud-card__provider">{ambiente.provider_label}</span>
      <span className="cloud-card__nome" title={ambiente.label}>{accorcia(ambiente.label, 30)}</span>
      {idDiverso && <span className="cloud-card__id mono" title={ambiente.account}>{accorcia(ambiente.account, 34)}</span>}
      <span className="cloud-card__numeri">
        <span className="cloud-num">
          <span className="cloud-num__v">{s.instances_running}<span className="venv-kpi__den">/{s.instances}</span></span>
          <span className="cloud-num__l">{t('Istanze')}</span>
        </span>
        <span className="cloud-num">
          <span className="cloud-num__v">{s.networks}</span>
          <span className="cloud-num__l">{t('Reti')}</span>
        </span>
        <span className="cloud-num">
          <span className="cloud-num__v">{ambiente.regions.length}</span>
          <span className="cloud-num__l">{t('Regioni')}</span>
        </span>
      </span>
      {altri.length > 0 && <span className="venv-card__conta" title={altri.join(' · ')}>{altri.join(' · ')}</span>}
      <span className="cloud-card__piede">
        {s.public > 0 && <span className="badge badge--warn">{t('{n} esposte', { n: s.public })}</span>}
        {s.open_security_groups > 0 && <span className="badge badge--error">{t('{n} SG aperti', { n: s.open_security_groups })}</span>}
        {ambiente.cost_month ? (
          <Link to={`/cost-center?account=${encodeURIComponent(ambiente.key)}`} onClick={(e) => e.stopPropagation()}>
            {t('{c} questo mese', { c: denaro(ambiente.cost_month.amount, ambiente.cost_month.currency) })}
          </Link>
        ) : (
          <span className="muted">{t('costi non raccolti')}</span>
        )}
      </span>
      {ambiente.last_seen && <span className="venv-card__fonte">{t('visto {quando}', { quando: timeAgo(ambiente.last_seen) })}</span>}
    </div>
  )
}

// ----------------------------------------------------------------- la mappa

const GENERI: { kind: CloudKind; label: () => string; colore: string; gruppo: GruppoNodo }[] = [
  { kind: 'account', label: () => t('Account'), colore: '#7c3aed', gruppo: 'cloud' },
  { kind: 'region', label: () => t('Regione'), colore: '#6366f1', gruppo: 'cloud' },
  { kind: 'network', label: () => t('Rete (VPC/VNet)'), colore: '#2563eb', gruppo: 'rete' },
  { kind: 'subnet', label: () => t('Subnet'), colore: '#0ea5e9', gruppo: 'rete' },
  { kind: 'instance', label: () => t('Istanza'), colore: '#16a34a', gruppo: 'server' },
  { kind: 'database', label: () => t('Base dati'), colore: '#0d9488', gruppo: 'server' },
  { kind: 'load_balancer', label: () => t('Bilanciatore'), colore: '#ea580c', gruppo: 'rete' },
  { kind: 'function', label: () => t('Funzione'), colore: '#ca8a04', gruppo: 'cloud' },
  { kind: 'bucket', label: () => t('Storage'), colore: '#d97706', gruppo: 'storage' },
  { kind: 'volume', label: () => t('Volume'), colore: '#a16207', gruppo: 'storage' },
  { kind: 'security_group', label: () => t('Gruppo di sicurezza'), colore: '#dc2626', gruppo: 'firewall' },
  { kind: 'other', label: () => t('Altro'), colore: '#64748b', gruppo: 'sconosciuto' },
]
const PER_GENERE = Object.fromEntries(GENERI.map((g) => [g.kind, g])) as Record<CloudKind, (typeof GENERI)[number]>
const STRUTTURA: CloudKind[] = ['account', 'region', 'network', 'subnet']

function MappaAccount({ ambiente }: { ambiente: CloudEnvironment }) {
  const [regione, setRegione] = useState('')
  const grafo = useAsync(() => api.cloudMapGraph(ambiente.key, regione || undefined), [ambiente.key, regione])
  const [layout, setLayout] = useState<LayoutMappa>(() => leggiLayout())
  const [spostati, setSpostati] = useState<Map<string, Posizione>>(new Map())
  const [nascosti, setNascosti] = useState<Set<CloudKind>>(new Set())
  const [cerca, setCerca] = useState('')
  const [scelto, setScelto] = useState<string | null>(null)

  useEffect(() => {
    setScelto(null)
    setSpostati(new Map())
  }, [regione])

  const g = grafo.data
  const presenti = useMemo(() => new Set((g?.nodes ?? []).map((n) => n.kind)), [g])

  const { nodi, archi } = useMemo(() => {
    if (!g) return { nodi: [] as NodoMappa[], archi: [] as ArcoMappa[] }
    const q = cerca.trim().toLowerCase()
    const visibili = g.nodes.filter((n) => !nascosti.has(n.kind))
    const ids = new Set(visibili.map((n) => n.id))
    const nodi: NodoMappa[] = visibili.map((n) => {
      const genere = PER_GENERE[n.kind] ?? PER_GENERE.other
      const sotto = [n.sotto, n.volumes ? t('{n} volumi', { n: n.volumes }) : null].filter(Boolean).join(' · ')
      return {
        id: n.id,
        label: n.label,
        sotto: sotto || null,
        colore: genere.colore,
        gruppo: genere.gruppo,
        livello: n.level,
        seme: n.kind === 'account',
        trovato: q !== '' && (n.label.toLowerCase().includes(q) || (n.sotto ?? '').toLowerCase().includes(q)),
        spento: n.kind === 'instance' && n.running === false,
        segno: n.public ? '🌐' : undefined,
      }
    })
    const archi: ArcoMappa[] = g.edges
      .filter((e) => ids.has(e.src) && ids.has(e.dst))
      .map((e) => ({
        id: e.id,
        src: e.src,
        dst: e.dst,
        colore: e.kind === 'attached' ? '#a16207' : e.kind === 'contains' ? 'var(--border-strong, #98a2b3)' : '#ea580c',
        tratteggio: e.kind === 'attached' ? '4 3' : e.kind === 'contains' ? undefined : '6 4',
        titolo: e.kind === 'attached' ? t('volume collegato') : e.kind === 'contains' ? t('contiene') : e.kind,
      }))
    return { nodi, archi }
  }, [g, nascosti, cerca])

  const legenda: VoceLegenda[] = GENERI.filter((x) => presenti.has(x.kind)).map((x) => ({
    etichetta: x.label(), colore: x.colore, forma: 'punto' as const,
  }))
  const trovati = nodi.filter((n) => n.trovato).map((n) => n.id)
  const nodoScelto = g?.nodes.find((n) => n.id === scelto) ?? null

  return (
    <div className="vmap vmap--due" style={{ gridTemplateColumns: 'minmax(0, 3fr) minmax(260px, 1fr)' }}>
      <Card
        title={t('{account} · mappa', { account: accorcia(ambiente.label, 40) })}
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={regione} onChange={(e) => setRegione(e.target.value)} aria-label={t('Regione')}>
              <option value="">{t('Tutte le regioni')}</option>
              {ambiente.regions.map((r) => (
                <option key={r.name} value={r.name}>{r.name} ({r.resources})</option>
              ))}
            </select>
            <input type="search" placeholder={t('Cerca nella mappa…')} value={cerca}
                   onChange={(e) => setCerca(e.target.value)} style={{ maxWidth: 200 }} />
            <Link to={`/cinventory?account=${encodeURIComponent(ambiente.key)}`}>{t('Inventario')}</Link>
          </div>
        }
      >
        <p style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '0 0 8px' }}>
          {GENERI.filter((x) => presenti.has(x.kind) && !STRUTTURA.includes(x.kind)).map((x) => {
            const acceso = !nascosti.has(x.kind)
            return (
              <button key={x.kind} type="button" className={`chip${acceso ? ' chip--ok' : ''}`}
                      style={{ cursor: 'pointer', borderColor: x.colore, opacity: acceso ? 1 : 0.5 }}
                      title={acceso ? t('Nascondi') : t('Mostra')}
                      onClick={() => setNascosti((prima) => {
                        const dopo = new Set(prima)
                        if (dopo.has(x.kind)) dopo.delete(x.kind)
                        else dopo.add(x.kind)
                        return dopo
                      })}>
                <span style={{ color: x.colore }}>●</span> {x.label()}
              </button>
            )
          })}
        </p>
        <ErrorBanner error={grafo.error} />
        {grafo.loading && !g ? (
          <Spinner />
        ) : g && nodi.length > 0 ? (
          <>
            {g.volumes_collapsed && (
              <p className="field__hint">{t('Molte risorse: i volumi sono contati sulla loro istanza invece che disegnati.')}</p>
            )}
            <Mappa
              nodi={nodi}
              archi={archi}
              layout={layout}
              onLayout={(l) => {
                setLayout(l)
                scriviLayout(l)
                setSpostati(new Map())
              }}
              nomiFasce={(livello) =>
                [t('Account'), t('Regioni'), t('Reti'), t('Subnet'), t('Risorse'), t('Volumi')][livello] ?? t('Livello {n}', { n: livello })
              }
              fissati={spostati}
              onFissa={(id, p) => setSpostati((prima) => new Map(prima).set(id, p))}
              selezionato={scelto}
              onClickNodo={(id) => setScelto(id)}
              onClickSfondo={() => setScelto(null)}
              inquadra={trovati.length ? trovati : undefined}
              legenda={legenda}
              etichetta={t('Mappa cloud di {account}: {n} nodi', { account: ambiente.label, n: nodi.length })}
              altezza={620}
            />
          </>
        ) : (
          <p className="muted">{t('Nessuna risorsa da mostrare con questi filtri.')}</p>
        )}
      </Card>
      <PannelloNodo nodo={nodoScelto} />
    </div>
  )
}

const ETICHETTE_INFO: Record<string, () => string> = {
  instance_type: () => t('Taglia'),
  machine_type: () => t('Taglia'),
  shape: () => t('Taglia'),
  state: () => t('Stato'),
  power_state: () => t('Alimentazione'),
  mgmt_ip: () => t('IP privato'),
  public_ip: () => t('IP pubblico'),
  cidr_block: () => t('CIDR'),
  size_gb: () => t('Dimensione (GB)'),
  engine: () => t('Motore'),
  engine_version: () => t('Versione'),
  instance_class: () => t('Classe'),
  scheme: () => t('Schema'),
  dns_name: () => t('Nome DNS'),
  runtime: () => t('Runtime'),
  rules_open_to_internet: () => t('Regole aperte a Internet'),
  encrypted: () => t('Cifrato'),
  publicly_accessible: () => t('Accessibile da Internet'),
  availability_zone: () => t('Zona'),
  zone: () => t('Zona'),
  resource_group: () => t('Gruppo di risorse'),
}

function valore(v: unknown): string {
  if (typeof v === 'boolean') return v ? t('sì') : t('no')
  if (Array.isArray(v)) return v.join(', ')
  return String(v)
}

function PannelloNodo({ nodo }: { nodo: CloudMapNode | null }) {
  const dettaglio = useAsync(
    () => (nodo?.ci_id ? api.cloudMapResource(nodo.ci_id) : Promise.resolve(null)),
    [nodo?.ci_id],
  )
  if (!nodo) {
    return (
      <Card title={t('Dettaglio')}>
        <p className="muted">{t('Scegli un nodo sulla mappa: qui compaiono i suoi dati. La rotella ingrandisce, il trascinamento sposta, il passaggio sopra un nodo accende i suoi collegamenti.')}</p>
      </Card>
    )
  }
  const genere = PER_GENERE[nodo.kind] ?? PER_GENERE.other
  const d = dettaglio.data
  return (
    <Card title={nodo.label}>
      <p style={{ margin: '0 0 8px' }}>
        <span className="chip" style={{ borderColor: genere.colore }}><span style={{ color: genere.colore }}>●</span> {genere.label()}</span>{' '}
        {nodo.public && <span className="badge badge--warn">{t('Esposta a Internet')}</span>}{' '}
        {nodo.running === false && <span className="badge">{t('Spenta')}</span>}
      </p>
      {dettaglio.loading && !d ? <Spinner /> : d ? (
        <dl className="kv">
          <dt>{t('Tipo')}</dt><dd className="mono">{d.resource_type}</dd>
          {d.region && (<><dt>{t('Regione')}</dt><dd>{d.region}</dd></>)}
          {Object.entries(d.info).map(([k, v]) => (
            <FragmentKV key={k} k={ETICHETTE_INFO[k]?.() ?? k} v={valore(v)} />
          ))}
        </dl>
      ) : nodo.sotto ? <p className="mono">{nodo.sotto}</p> : null}
      {nodo.ci_id && <p><Link to={`/cis/${nodo.ci_id}`}>{t('Apri la scheda del CI')}</Link></p>}
    </Card>
  )
}

function FragmentKV({ k, v }: { k: string; v: string }) {
  return (
    <>
      <dt>{k}</dt>
      <dd className="mono">{v}</dd>
    </>
  )
}
